import { useMutation } from '@apollo/client/react'
import {
  Alert,
  Box,
  Button,
  Center,
  Heading,
  HStack,
  Icon,
  Input,
  InputProps,
  Link,
  NativeSelect,
  Separator,
  SimpleGrid,
  Spinner,
  Text,
  TextareaProps,
  VStack,
  Field,
} from '@chakra-ui/react'
import { AutoResizeTextarea } from '@src/components/sco/AutoResizeTextarea'
import { GenericFormProps, processSubmit } from '@src/components/sco/Form/formHelpers'
import { FormContainer } from '@src/components/sco/FormContainer'
import { UploadDocumentButton } from '@src/components/sco/UploadDocumentButton'
import { ChevronRightIcon } from '@src/components/sco/icons'
import { OrDivider } from '@src/components/sco/utilities/OrDivider'
import { FUNDING_STAGES } from '@src/constants/funding-stages'
import industries from '@src/constants/industries'
import { UpsertFgProfilePayload } from '@src/graphql-types'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { TrackEvent } from '@src/lib/TrackEvent'
import { gql } from '@src/lib/gql'
import { useRouter } from 'next/navigation'
import React, { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { LuFile, LuUpload } from 'react-icons/lu'

type UserSignupPitchDeckFormFields = {
  companyFundingGoalInDollars?: string
  companyFundingStageKey?: string
  companyIndustryKey?: string
  companyLocation?: string
  companyLocationCity?: string
  companyLocationCountryKey?: string
  companyLocationState?: string
  companyName?: string
  companyPitchDeckNote?: string
  companyPitchDeckUrl?: string
}

type UserSignupPitchDeckFormProps = GenericFormProps<UserSignupPitchDeckFormFields> & {
  title?: string
  returnToHref?: string
  hubspotFormKey?: string
}

export const UserSignupPitchDeckForm = (props: UserSignupPitchDeckFormProps) => {
  const router = useRouter()
  const { currentUser, refetchCurrentUser } = useCurrentUser()
  const [showPitchDeckUpload, setShowPitchDeckUpload] = useState<boolean>(true)
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false)
  const [formErrorMessage, setFormErrorMessage] = useState<string>(null)
  const form = useForm<UserSignupPitchDeckFormFields>({
    mode: 'onSubmit',
    defaultValues: {
      companyFundingGoalInDollars: currentUser?.company?.fundingGoalInDollars
        ? `${currentUser?.company?.fundingGoalInDollars}`
        : null,
      companyFundingStageKey: currentUser?.company?.fundingStageKey,
      companyIndustryKey: currentUser?.company?.industryKey,
      companyLocation: currentUser?.company?.location,
      companyLocationCity: currentUser?.company?.locationCity,
      companyLocationCountryKey: currentUser?.company?.locationCountryKey,
      companyLocationState: currentUser?.company?.locationState,
      companyName: currentUser?.company?.name,
      companyPitchDeckNote: currentUser?.company?.pitchDeckNote,
      companyPitchDeckUrl: currentUser?.company?.pitchDeckUrl,
    },
  })
  const [pitchDeckUrl] = form.watch(['companyPitchDeckUrl'])

  const [submitPitchDeckRequest, { loading, error }] = useMutation(gql`
    mutation submitPitchDeckRequestMutation($input: SubmitPitchDeckRequestInput!) {
      submitPitchDeckRequest(input: $input) {
        user {
          id
          company {
            id
            industryKey
            fundingGoalInDollars
            fundingStageKey
            location
            locationCity
            locationState
            locationCountryKey
            pitchDeckNote
            pitchDeckUrl
            name
          }
        }
      }
    }
  `)

  const handleSubmit = useCallback(
    async (values: UserSignupPitchDeckFormFields) =>
      processSubmit({
        props,
        values,
        setFormErrorMessage,
        action: async (values: UserSignupPitchDeckFormFields) => {
          return submitPitchDeckRequest({
            variables: {
              input: {
                companyFundingGoalInDollars: values.companyFundingGoalInDollars,
                companyFundingStageKey: values.companyFundingStageKey,
                companyIndustryKey: values.companyIndustryKey,
                companyLocation: values.companyLocation,
                companyLocationCity: values.companyLocationCity,
                companyLocationCountryKey: values.companyLocationCountryKey,
                companyLocationState: values.companyLocationState,
                companyName: values.companyName,
                companyPitchDeckNote: values.companyPitchDeckNote,
                companyPitchDeckUrl: values.companyPitchDeckUrl,
                hubspotFormKey: props.hubspotFormKey,
              },
            },
          })
        },
        afterSuccess: async (values: UserSignupPitchDeckFormFields, result: UpsertFgProfilePayload) => {
          if (props.returnToHref) {
            setIsRedirecting(true)
            await refetchCurrentUser()
            if (props.afterSuccess) {
              await props.afterSuccess(values, result)
            }
            router.push(props.returnToHref)
          } else {
            await refetchCurrentUser()
            if (props.afterSuccess) {
              await props.afterSuccess(values, result)
            }
          }
        },
      }),
    []
  )

  if (!currentUser) {
    return (
      <FormContainer {...props.containerProps}>
        <Center>
          <Spinner />
        </Center>
      </FormContainer>
    )
  }

  const isUploadedFile = pitchDeckUrl?.indexOf('https://scoaccounts') === 0

  return (
    <FormContainer {...props.containerProps}>
      <TrackEvent action={'s2Viewed'} onlyOnScreen={true} />
      <Heading marginBottom={'1em'} size={'lg'}>
        {props.title || (showPitchDeckUpload ? 'Upload Your Pitch Deck' : 'Tell Us More')}
      </Heading>
      <VStack asChild align={'stretch'} gap={'1.25em'}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          {formErrorMessage && (
            <Alert.Root borderRadius={'md'} status={'error'}>
              <Alert.Indicator />
              <Box>{formErrorMessage}</Box>
            </Alert.Root>
          )}
          {showPitchDeckUpload ? (
            <>
              <Box
                backgroundColor={'whiteAlpha.100'}
                border={'1px solid'}
                borderColor={'whiteAlpha.100'}
                borderRadius={'md'}
                padding={'1.5em'}
              >
                <HStack gap={'1em'} marginBottom={'1em'}>
                  <Icon asChild color={'white'} fontSize={'2em'}>
                    <LuFile />
                  </Icon>
                  {isUploadedFile ? (
                    <Text flexGrow={1} wordBreak={'break-word'}>
                      <Link href={form.getValues().companyPitchDeckUrl} target={'_blank'}>
                        {renderFileName(pitchDeckUrl)}
                      </Link>
                    </Text>
                  ) : (
                    <Text flexGrow={1}>PDF or Powerpoint preferred, 40MB Max.</Text>
                  )}
                </HStack>
                {isUploadedFile ? (
                  <Box
                    borderTop={'1px solid'}
                    borderTopColor={'whiteAlpha.200'}
                    paddingTop={'1em'}
                    textAlign={'center'}
                  >
                    <Link variant={'underline'} onClick={() => form.setValue('companyPitchDeckUrl', '')}>
                      Remove
                    </Link>
                  </Box>
                ) : (
                  <UploadDocumentButton
                    variant={'cta'}
                    width={'full'}
                    storedFile={{
                      attachmentType: 'User',
                      attachmentId: currentUser?.userId,
                      contextKey: 'investor-outreach-pitch-deck',
                    }}
                    onError={err => alert(err?.toString() || 'Upload failed.')}
                    onSuccess={url => form.setValue('companyPitchDeckUrl', url)}
                  >
                    <LuUpload />
                    Upload Pitch Deck
                  </UploadDocumentButton>
                )}
              </Box>

              {!isUploadedFile && (
                <>
                  <OrDivider />

                  <Field.Root invalid={!!form.formState.errors.companyPitchDeckUrl}>
                    <Field.Label>Provide a URL to your Pitch Deck:</Field.Label>

                    <Input
                      placeholder={'Ex: https://drive.google.com/...'}
                      type={'text'}
                      {...(props.inputProps as InputProps)}
                      {...form.register('companyPitchDeckUrl', {})}
                    />
                    <Field.ErrorText>{form.formState.errors.companyPitchDeckUrl?.message}</Field.ErrorText>
                    <Text fontSize={'0.8em'} marginY={'0.5em'}>
                      Please make sure this link is not restricted or password-protected.
                    </Text>
                  </Field.Root>
                </>
              )}

              <Field.Root invalid={!!form.formState.errors.companyPitchDeckNote}>
                <Field.Label>Any comments to add for our review team?</Field.Label>

                {/* @ts-ignore */}
                <AutoResizeTextarea
                  {...(props.inputProps as TextareaProps)}
                  {...form.register('companyPitchDeckNote', {})}
                />
                <Field.ErrorText>{form.formState.errors.companyPitchDeckNote?.message}</Field.ErrorText>
              </Field.Root>
            </>
          ) : (
            <>
              <SimpleGrid columns={[1, 1, 2]} gap={'1em'}>
                <Field.Root invalid={!!form.formState.errors.companyName}>
                  <Field.Label>Startup Name</Field.Label>
                  <Input
                    placeholder={'Acme Inc.'}
                    type={'text'}
                    {...(props.inputProps as InputProps)}
                    {...form.register('companyName', {})}
                  />
                  <Field.ErrorText>{form.formState.errors.companyName?.message}</Field.ErrorText>
                </Field.Root>

                <Field.Root invalid={!!form.formState.errors.companyIndustryKey}>
                  <Field.Label>Industry</Field.Label>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      placeholder={'Select One'}
                      type={'text'}
                      {...(props.inputProps as React.SelectHTMLAttributes<HTMLSelectElement>)}
                      {...form.register('companyIndustryKey', {})}
                    >
                      {industries.map((i, idx) => (
                        <option key={idx} value={i.key}>
                          {i.label}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                  <Field.ErrorText>{form.formState.errors.companyIndustryKey?.message}</Field.ErrorText>
                </Field.Root>
              </SimpleGrid>
              <SimpleGrid columns={[1, 1, 2]} gap={'1em'}>
                <Field.Root invalid={!!form.formState.errors.companyFundingGoalInDollars}>
                  <Field.Label>Funding Goal ($USD)</Field.Label>
                  <Input
                    placeholder={'250,000'}
                    type={'text'}
                    {...(props.inputProps as InputProps)}
                    {...form.register('companyFundingGoalInDollars', {})}
                  />
                  <Field.ErrorText>
                    {form.formState.errors.companyFundingGoalInDollars?.message}
                  </Field.ErrorText>
                </Field.Root>

                <Field.Root invalid={!!form.formState.errors.companyFundingStageKey}>
                  <Field.Label>Fundraise Stage</Field.Label>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      placeholder={'Select One'}
                      type={'text'}
                      {...(props.inputProps as React.SelectHTMLAttributes<HTMLSelectElement>)}
                      {...form.register('companyFundingStageKey', {})}
                    >
                      {FUNDING_STAGES.map((i, idx) => (
                        <option key={idx} value={i.key}>
                          {i.title}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                  <Field.ErrorText>{form.formState.errors.companyFundingStageKey?.message}</Field.ErrorText>
                </Field.Root>
              </SimpleGrid>

              <Field.Root invalid={!!form.formState.errors.companyLocation}>
                <Field.Label>Location</Field.Label>
                <Input
                  placeholder={'City, State, Country'}
                  type={'text'}
                  {...(props.inputProps as InputProps)}
                  {...(form.register('companyLocation', {}) as any)}
                />
                <Field.ErrorText>{form.formState.errors.companyLocation?.message}</Field.ErrorText>
              </Field.Root>

              <Field.Root invalid={!!form.formState.errors.companyPitchDeckNote}>
                <Field.Label>Any comments to add for our review team?</Field.Label>

                {/* @ts-ignore */}
                <AutoResizeTextarea
                  {...(props.inputProps as TextareaProps)}
                  {...form.register('companyPitchDeckNote', {})}
                />
                <Field.ErrorText>{form.formState.errors.companyPitchDeckNote?.message}</Field.ErrorText>
              </Field.Root>
            </>
          )}
          <Box paddingTop={'0.5em'}>
            <Button
              loading={form.formState.isSubmitting || isRedirecting}
              size={'lg'}
              type={'submit'}
              variant={'cta'}
              width={'full'}
              {...props.submitButtonProps}
            >
              Submit
              <ChevronRightIcon />
            </Button>
          </Box>
          <Separator />
          <Box textAlign={'center'}>
            <Link variant={'underline'} onClick={() => setShowPitchDeckUpload(!showPitchDeckUpload)}>
              {showPitchDeckUpload ? "I don't have a pitch deck yet." : 'Upload pitch deck instead.'}
            </Link>
          </Box>
        </form>
      </VStack>
    </FormContainer>
  )
}

export function renderFileName(url) {
  let filename = 'File'

  try {
    filename = `${url}`.split('/').pop().replaceAll('%20', ' ')
  } catch (err) {
    console.log(err)
  }

  return filename
}
